/** Query param for unauthenticated redirects to the login landing (`/`). */
export const AUTH_REASON_PARAM = "auth";

/**
 * Cognito OAuth callback params on the SPA URL after Hosted UI / federated redirect.
 * Amplify exchanges `code` then we strip these so the address bar stays clean.
 */
export const OAUTH_CALLBACK_PARAMS = [
  "code",
  "state",
  "session_state",
  "iss",
  "error",
  "error_description",
] as const;

export type AuthReason = "required" | "expired";

const MESSAGE_KEYS: Record<AuthReason, string> = {
  required: "auth.reason.required",
  expired: "auth.reason.expired",
};

export function resolveAuthReason(hasAuthError: boolean): AuthReason {
  return hasAuthError ? "expired" : "required";
}

/** Returns an i18n key for a known auth reason, otherwise undefined. */
export function authReasonMessageKey(
  reason: string | null,
): string | undefined {
  if (reason === "required" || reason === "expired") {
    return MESSAGE_KEYS[reason];
  }
  return undefined;
}

/**
 * Drop login-reason always. Strip OAuth callback params only when the URL looks
 * like a Cognito return (`state` + `code` or `error`).
 */
export function stripAuthRedirectParams(params: URLSearchParams): void {
  params.delete(AUTH_REASON_PARAM);
  if (!hasOAuthCallbackParams(params)) return;
  for (const key of OAUTH_CALLBACK_PARAMS) {
    params.delete(key);
  }
}

/** True for Cognito OAuth returns: `state` with either `code` or `error`. */
export function hasOAuthCallbackParams(params: URLSearchParams): boolean {
  return params.has("state") && (params.has("code") || params.has("error"));
}

/** Build `/?auth=…` for the login landing, preserving unrelated search params. */
export function buildLoginRedirectSearch(
  currentSearch: string,
  reason: AuthReason,
): string {
  const params = new URLSearchParams(currentSearch);
  stripAuthRedirectParams(params);
  params.set(AUTH_REASON_PARAM, reason);
  return `?${params.toString()}`;
}
